import api from '../../api/restaurant/restaurantOrderApi';
import {
  AddRestaurantOrderItemRequest,
  CreateRestaurantOrderRequest,
  CustomResponse,
  RestaurantOrderCheckDTO,
  RestaurantOrderDTO,
  RestaurantOrderItemDTO,
  RestaurantOrderSummaryDTO,
} from '../../types/restaurant/RestaurantOrderTypes';
import { SaleDetail } from '../../types/sale';

export interface RestaurantSplitCheckInput {
  label?: string;
  itemIds: number[];
}

export interface RestaurantOrderBillingRequest {
  documentTypeId: number;
}

export type RestaurantBillingStatus =
  | 'PENDING'
  | 'FINALIZED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'CANCELLED';

export interface RestaurantOrderBillingDTO {
  id: number;
  orderId: number;
  saleId: number;
  status: RestaurantBillingStatus;
  createdAt: string;
  finalizedAt?: string;
  acceptedAt?: string;
  rejectedAt?: string;
  continuation?: SaleDetail;
  reason?: string;
  message?: string;
}

type RestaurantOrderFilters = Record<string, string | number | undefined>;

const buildQueryString = (filters: RestaurantOrderFilters = {}) => {
  const query = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      query.set(key, String(value));
    }
  });

  return query.size ? `?${query.toString()}` : '';
};

const RestaurantOrderService = {
  // Consultas
  list: async (branchId: number, filters: RestaurantOrderFilters = {}) => {
    const suffix = buildQueryString(filters);

    return (await api.fetchCustom(`/branches/${branchId}${suffix}`))
      .data as CustomResponse<RestaurantOrderDTO[]>;
  },

  get: async (id: number) => {
    return (await api.fetchById(id)).data as CustomResponse<RestaurantOrderDTO>;
  },

  activeByTable: async (tableId: number) => {
    return (await api.fetchCustom(`/tables/${tableId}/active`))
      .data as CustomResponse<RestaurantOrderDTO>;
  },

  // Apertura
  create: async (branchId: number, body: CreateRestaurantOrderRequest) => {
    return (await api.addCustom(`/branches/${branchId}`, body))
      .data as CustomResponse<RestaurantOrderDTO>;
  },

  // Ítems
  addItem: async (orderId: number, body: AddRestaurantOrderItemRequest) => {
    return (await api.addCustom(`/${orderId}/items`, body))
      .data as CustomResponse<RestaurantOrderDTO>;
  },

  updateQuantity: async (orderId: number, itemId: number, quantity: number) => {
    return (
      await api.patchCustom(`${orderId}/items/${itemId}/quantity`, {
        quantity,
      })
    ).data as CustomResponse<RestaurantOrderDTO>;
  },

  cancelItem: async (orderId: number, itemId: number) => {
    return (await api.addCustom(`/${orderId}/items/${itemId}/cancel`, {}))
      .data as CustomResponse<RestaurantOrderDTO>;
  },

  updateComboQuantity: async (
    orderId: number,
    comboInstanceId: string,
    quantity: number
  ) => {
    return (
      await api.patchCustom(
        `${orderId}/combos/${comboInstanceId}/quantity`,
        { quantity }
      )
    ).data as CustomResponse<RestaurantOrderDTO>;
  },

  changeCustomer: async (orderId: number, customerId: number) => {
    return (
      await api.patchCustom(`${orderId}/customer`, {
        customerId,
      })
    ).data as CustomResponse<RestaurantOrderDTO>;
  },

  // Estados
  confirm: async (orderId: number) => {
    return (await api.addCustom(`/${orderId}/confirm`, {}))
      .data as CustomResponse<RestaurantOrderDTO>;
  },

  reopen: async (orderId: number) => {
    return (await api.addWithoutData(`/${orderId}/reopen`))
      .data as CustomResponse<RestaurantOrderDTO>;
  },

  cancel: async (orderId: number) => {
    return (await api.addCustom(`/${orderId}/cancel`, {}))
      .data as CustomResponse<RestaurantOrderDTO>;
  },

  // Facturación / envío a caja
  billing: async (orderId: number, body: RestaurantOrderBillingRequest) => {
    return (await api.addCustom(`/${orderId}/billing`, body))
      .data as CustomResponse<SaleDetail>;
  },

  getBilling: async (orderId: number) => {
    return (await api.fetchCustom(`/${orderId}/billing`))
      .data as CustomResponse<RestaurantOrderBillingDTO>;
  },

  // Configuración del restaurante: traslado / mover ítems / fusión / división

  orderItems: async (
    orderId: number,
    filters: { search?: string; movableOnly?: boolean } = {}
  ) => {
    const query = new URLSearchParams();
    if (filters.search?.trim()) query.set('search', filters.search.trim());
    if (filters.movableOnly) query.set('movableOnly', 'true');
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return (await api.fetchCustom(`/${orderId}/items${suffix}`))
      .data as CustomResponse<RestaurantOrderItemDTO[]>;
  },

  transferTargets: async (
    orderId: number,
    filters: { diningRoomId?: number; search?: string } = {}
  ) => {
    const query = new URLSearchParams();
    if (filters.diningRoomId)
      query.set('diningRoomId', String(filters.diningRoomId));
    if (filters.search?.trim()) query.set('search', filters.search.trim());
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return (await api.fetchCustom(`/${orderId}/transfer-targets${suffix}`))
      .data as CustomResponse<RestaurantOrderSummaryDTO[]>;
  },

  transferTable: async (orderId: number, targetTableId: number) => {
    return (await api.addCustom(`/${orderId}/transfer-table`, { targetTableId }))
      .data as CustomResponse<RestaurantOrderDTO>;
  },

  moveItems: async (
    orderId: number,
    targetOrderId: number,
    itemIds: number[]
  ) => {
    return (
      await api.addCustom(`/${orderId}/move-items`, { targetOrderId, itemIds })
    ).data as CustomResponse<RestaurantOrderDTO>;
  },

  merge: async (orderId: number, sourceOrderId: number) => {
    return (await api.addCustom(`/${orderId}/merge`, { sourceOrderId }))
      .data as CustomResponse<RestaurantOrderDTO>;
  },

  listChecks: async (orderId: number) => {
    return (await api.fetchCustom(`/${orderId}/checks`))
      .data as CustomResponse<RestaurantOrderCheckDTO[]>;
  },

  splitChecks: async (orderId: number, checks: RestaurantSplitCheckInput[]) => {
    return (await api.addCustom(`/${orderId}/checks/split`, { checks }))
      .data as CustomResponse<RestaurantOrderDTO>;
  },

  clearChecks: async (orderId: number) => {
    return (await api.addCustom(`/${orderId}/checks/clear`, {}))
      .data as CustomResponse<RestaurantOrderDTO>;
  },

  billCheck: async (
    orderId: number,
    checkId: number,
    documentTypeId: number
  ) => {
    return (
      await api.addCustom(`/${orderId}/checks/${checkId}/billing`, {
        documentTypeId,
      })
    ).data as CustomResponse<SaleDetail>;
  },

  getCheckBilling: async (orderId: number, checkId: number) => {
    return (await api.fetchCustom(`/${orderId}/checks/${checkId}/billing`))
      .data as CustomResponse<RestaurantOrderBillingDTO>;
  },
};

export default RestaurantOrderService;
